import colorVars from "@/assets/styles/colorVars"
import OmnichannelEmailSrc from "@/assets/svg/animation/omnichannel-email.inline.svg"
import OmnichannelSmsSrc from "@/assets/svg/animation/omnichannel-sms.inline.svg"
import OmnichannelVoiceSrc from "@/assets/svg/animation/omnichannel-voice.inline.svg"
import OmnichannelWebSrc from "@/assets/svg/animation/omnichannel-web.inline.svg"
import { AnimeTimelineInstance } from "animejs"
import anime from "animejs"
import React, { FC, useEffect, useRef, useState } from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"

import { Audio } from "./Audio"
import { Email } from "./Email"
import { Sms } from "./Sms"
import { Web } from "./Web"

export type Omnichannel = "email" | "sms" | "voice" | "web"

export interface ISequenceTiming {
  audioMessagePlaybackDuration: number
  timeBeforeTransitioning: number
  timeBetweenFullReplay: number
  timeBetweenScenes: number
}

export interface IAnimationProps {
  animationId: string
  contentRef?: any
  scenes: any
}

export interface AnimationPlayerProps {
  animationData: IAnimationProps
}

// Timing constants (in ms)
const sequenceTiming: ISequenceTiming = {
  // How long an audio clip takes to play
  audioMessagePlaybackDuration: 5300,
  // How long between a finished scene and the next transition
  timeBeforeTransitioning: 6000,
  // How long to wait before the whole cycle repeats
  timeBetweenFullReplay: 5250,
  // How long the transition takes
  timeBetweenScenes: 2000,
}

// Player frame
const AnimationFrame = styled.div<{ isLoaded?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: center;
  max-width: 560px;
  height: 100%;
  min-height: 660px;
  margin: 0 auto;
  opacity: 0;
  transition: opacity 300ms ease;

  ${({ isLoaded }) =>
    isLoaded &&
    css`
      opacity: 1;
    `}

  ${up("md")} {
    flex-direction: row;
    gap: 0;
    align-items: center;
    overflow: hidden;
  }
`

const BaseOmnichannelHighlightStyles = css`
  position: absolute;
  display: block;
  content: "";
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.12) 0%,
    rgba(196, 196, 196, 0.02) 100%
  );
  opacity: 0;
  transition: opacity 300ms ease-in-out, transform 300ms ease-in-out;
`

// Omnichannel indicator on top left
const OmnichannelIndicator = styled.div`
  position: relative;
  top: 0;
  left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.24) 0%,
    rgba(196, 196, 196, 0.04) 100%
  );
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
  transition: box-shadow 660ms ease-in-out;

  ${up("md")} {
    position: absolute;
    top: 32px;
    left: 32px;
  }

  &::before {
    ${BaseOmnichannelHighlightStyles}

    top: -6px;
    left: -6px;
    z-index: -2;
    width: calc(100% + 12px);
    height: calc(100% + 12px);
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(196, 196, 196, 0.02) 100%
    );
    border-radius: 12px;
    transform: scale(0.8);
  }

  &::after {
    ${BaseOmnichannelHighlightStyles}

    top: -12px;
    left: -12px;
    z-index: -3;
    width: calc(100% + 24px);
    height: calc(100% + 24px);
    background: linear-gradient(
      -135deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(196, 196, 196, 0.02) 100%
    );
    border-radius: 16px;
    transform: scale(0.75);
  }

  &.is-exploded {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.8),
      0 0 10px 0 rgba(255, 255, 255, 0.35), 0 0 20px 0 rgba(124, 107, 224, 0.2);

    &::before {
      opacity: 1;
      transform: scale(1.025);
    }

    &::after {
      opacity: 1;
      transform: scale(1.05);
    }
  }
`
const OmnichannelIconWrapper = styled.div<any>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;

  &::before {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: -2;
    display: block;
    width: 32px;
    height: 32px;
    content: "";
    background: ${colorVars.violet.p11};
    filter: blur(12px);
  }
`

// Animation staging and transition
const AnimationStage = styled.div`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
`
const Scene = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`
const FirstScene = styled(Scene)``
const SecondScene = styled(Scene)``

export const AnimationPlayer: FC<AnimationPlayerProps> = ({
  animationData,
}) => {
  const { animationId, scenes } = animationData

  // Refs
  const OmnichannelIndicatorRef = useRef<HTMLDivElement>(null)
  const omnichannelTimelineRef = useRef<AnimeTimelineInstance>()
  const OmnichannelIconWrapperRef = useRef<HTMLDivElement>(null)
  const firstSceneTimelineRef = useRef<AnimeTimelineInstance>()
  const FirstSceneRef = useRef<HTMLDivElement>(null)
  const secondSceneTimelineRef = useRef<AnimeTimelineInstance>()
  const SecondSceneRef = useRef<HTMLDivElement>(null)

  // State
  const [resetKey, setResetKey] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentChannel, setCurrentChannel] = useState<Omnichannel>(
    scenes[0].omnichannel,
  )

  // Check if the animation is multi-scene
  const isAnimMultiScene = scenes.length > 1

  // Handle effects
  useEffect(() => {
    // Instance omnichannel timeline
    omnichannelTimelineRef.current = anime.timeline({
      autoplay: true,
      easing: "easeInOutQuad",
      loop: false,
    })

    // Handle scene handoff if there's more than one
    const handleSceneHandoff = () => {
      // Trigger omnichannel transition
      transitionOmnichannel(scenes[1].omnichannel)
      // Dispatch event to trigger next scene
      window.dispatchEvent(new CustomEvent("pxmPlayFinalScene"))
    }

    if (isAnimMultiScene) {
      window.addEventListener(
        "pxmPreTransitionSceneComplete",
        handleSceneHandoff,
      )
    } else {
      // Dispatch event to trigger final scene (which is the only, in this conditional)
      window.dispatchEvent(new CustomEvent("pxmPlayFinalScene"))
    }

    // Restart entire animation when finished
    const restartAnimation = () => {
      // Fade out the first or second scene, depending on how many there are
      if (isAnimMultiScene) {
        secondSceneTimelineRef.current?.add({
          complete: () => {
            secondSceneTimelineRef.current?.pause()
            setResetKey((prevKey) => prevKey + 1)
          },
          duration: 600,
          opacity: [1, 0],
          scale: [1, 0.9],
          targets: [SecondSceneRef.current, OmnichannelIndicatorRef.current],
        })
        secondSceneTimelineRef.current?.play()
      } else {
        firstSceneTimelineRef.current?.add({
          complete: () => {
            firstSceneTimelineRef.current?.pause()
            setResetKey((prevKey) => prevKey + 1)
          },
          duration: 600,
          opacity: [1, 0],
          scale: [1, 0.9],
          targets: [FirstSceneRef.current],
        })
        firstSceneTimelineRef.current?.play()
      }

      // Dispatch event to trigger next animation in parent component
      window.dispatchEvent(new CustomEvent("pxmTriggerNextAnimation"))
    }
    window.addEventListener("pxmFullAnimComplete", restartAnimation)

    // Fade in when the component is loaded
    setIsLoaded(true)

    // Handle omnichannel transition
    const transitionOmnichannel = (target: Omnichannel) => {
      // Handle timeline animations
      omnichannelTimelineRef.current
        // Enlarge the indicator
        ?.add({
          changeBegin: () => {
            OmnichannelIndicatorRef.current?.classList.add("is-exploded")
          },
          duration: 1000,
          scale: [1, 1.1],
          targets: OmnichannelIndicatorRef.current,
        })
        // Transition out the icon
        .add(
          {
            duration: 90,
            easing: "easeOutQuad",
            opacity: [1, 0],
            scale: [1, 0],
            targets: OmnichannelIconWrapperRef.current,
          },
          "+=90",
        )
        // Set new icon state and transition it in
        .add(
          {
            changeBegin: () => {
              setCurrentChannel(target)
            },
            duration: 90,
            easing: "easeInQuad",
            opacity: [0, 1],
            scale: [0, 1],
            targets: OmnichannelIconWrapperRef.current,
          },
          "+=90",
        )
        // Shrink the indicator
        .add(
          {
            changeBegin: () =>
              OmnichannelIndicatorRef.current?.classList.remove("is-exploded"),
            complete: () => omnichannelTimelineRef.current?.pause(),
            delay: 90,
            duration: 660,
            scale: [1.1, 1],
            targets: OmnichannelIndicatorRef.current,
          },
          "+=900",
        )
    }

    // Handle first scene timeline fade-in animation
    firstSceneTimelineRef.current = anime.timeline({
      autoplay: true,
      easing: "easeOutCubic",
      loop: false,
    })
    firstSceneTimelineRef.current.add({
      duration: 800,
      opacity: [0, 1],
      scale: [0.9, 1],
      targets: [
        FirstSceneRef.current,
        isAnimMultiScene && OmnichannelIndicatorRef.current,
      ],
    })

    // Instantiate second scene timeline if there's more than one scene
    if (isAnimMultiScene) {
      secondSceneTimelineRef.current = anime.timeline({
        autoplay: false,
        easing: "easeOutCubic",
        loop: false,
      })
    }

    // Reset omnichannel indicator on component re-render
    setCurrentChannel(scenes[0].omnichannel)

    // Cleanup
    return () => {
      // Remove event listeners
      window.removeEventListener(
        "pxmPreTransitionSceneComplete",
        handleSceneHandoff,
      )
      window.removeEventListener("pxmFullAnimComplete", restartAnimation)

      // Clear timelines
      omnichannelTimelineRef.current?.pause()
      omnichannelTimelineRef.current = undefined
      firstSceneTimelineRef.current?.pause()
      firstSceneTimelineRef.current = undefined
      secondSceneTimelineRef.current?.pause()
      secondSceneTimelineRef.current = undefined
    }
  }, [resetKey])

  // Handle scene rendering logic
  const sceneRenderer = (scene: any) => {
    const sceneData = { ...scene, isAnimMultiScene, sequenceTiming }
    switch (scene.omnichannel) {
      case "email":
        return <Email {...sceneData} />
      case "sms":
        return <Sms {...sceneData} />
      case "voice":
        return <Audio {...sceneData} />
      case "web":
        return <Web {...sceneData} />
    }
  }

  return (
    <>
      <AnimationFrame id={animationId} isLoaded={isLoaded} key={resetKey}>
        <OmnichannelIndicator key={resetKey} ref={OmnichannelIndicatorRef}>
          <OmnichannelIconWrapper
            key={resetKey}
            ref={OmnichannelIconWrapperRef}>
            {currentChannel === "email" && <OmnichannelEmailSrc />}
            {currentChannel === "sms" && <OmnichannelSmsSrc />}
            {currentChannel === "voice" && <OmnichannelVoiceSrc />}
            {currentChannel === "web" && <OmnichannelWebSrc />}
          </OmnichannelIconWrapper>
        </OmnichannelIndicator>
        <AnimationStage>
          <FirstScene ref={FirstSceneRef}>
            {sceneRenderer(scenes[0])}
          </FirstScene>
          {isAnimMultiScene && (
            <SecondScene ref={SecondSceneRef}>
              {sceneRenderer(scenes[1])}
            </SecondScene>
          )}
        </AnimationStage>
      </AnimationFrame>
    </>
  )
}
